// Multipart upload with real progress (fetch can't report upload progress; XHR can).
// Resolves with the parsed JSON body; rejects with the server's error message when available.

/**
 * What a bare status code means to the person who was uploading.
 *
 * "Upload failed (408)" tells somebody who has just waited five minutes precisely nothing, and the
 * failures that reach here are the ones where knowing what to do next matters most — a 408 is worth
 * retrying, a 413 never is.
 */
function messageForStatus(status: number): string {
  if (status === 408) return 'The upload ran out of time before it finished. It is usually the connection - try again.';
  if (status === 413) return 'That file is too large for the lab to accept.';
  if (status === 401) return 'Your session has expired. Sign in again, then re-attach the file.';
  if (status === 0) return 'The connection dropped during the upload. Check the network and try again.';
  return `The upload failed (${status}).`;
}
/** What an upload is doing right now, for a progress line somebody can read. */
export interface UploadStats {
  pct: number;
  loaded: number;
  total: number;
  /** Bytes per second, averaged over the whole transfer so far. */
  bytesPerSecond: number;
  /** Seconds left at the current rate, or null before there is enough to estimate. */
  secondsLeft: number | null;
}

export function uploadWithProgress<T>(
  url: string,
  fd: FormData,
  onProgress?: (pct: number, stats?: UploadStats) => void,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url, true);
    xhr.withCredentials = true;
    xhr.responseType = 'json';
    const startedAt = Date.now();
    xhr.upload.onprogress = (e) => {
      if (!e.lengthComputable || !onProgress) return;
      const pct = Math.min(99, Math.round((e.loaded / e.total) * 100));
      const elapsed = (Date.now() - startedAt) / 1000;
      // Averaged rather than instantaneous: a per-event rate on a wobbly connection jumps around so
      // much that nobody can read it, and the average is what predicts the remaining time anyway.
      const bytesPerSecond = elapsed > 0.5 ? e.loaded / elapsed : 0;
      onProgress(pct, {
        pct,
        loaded: e.loaded,
        total: e.total,
        bytesPerSecond,
        secondsLeft: bytesPerSecond > 0 ? Math.round((e.total - e.loaded) / bytesPerSecond) : null,
      });
    };
    // No client-side ceiling: a big scan on a slow line is slow, not broken, and XHR's default of
    // 0 (never time out) is the right behaviour here. The server sets the real limit.
    xhr.timeout = 0;
    xhr.onerror = () => reject(new Error('The connection dropped during the upload. Check the network and try again.'));
    xhr.ontimeout = () => reject(new Error('The upload timed out. Try again on a steadier connection.'));
    xhr.onload = () => {
      const data = xhr.response as (T & { error?: string }) | null;
      if (xhr.status >= 200 && xhr.status < 300 && data) {
        onProgress?.(100);
        resolve(data);
      } else {
        reject(new Error((data && data.error) || messageForStatus(xhr.status)));
      }
    };
    xhr.send(fd);
  });
}
